import type { DimensionStyleTable } from "@dxf-ai-editor/cad-core";
import type {
  BlockDefinition,
  DrawingDocument,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { dimensionStyleTableFor } from "./blockExtentsCache";
import {
  blockInsertMetadataRefreshUpserts,
  changedBlockDefinitionClosure,
  dimensionBearingBlockClosure,
} from "./blockRedefinitionMetadataRefresh";
import { classifyEntityChange } from "./entityChangedClassify";
import {
  entityMetadataFromDrawingEntity,
  type EntityMetadata,
} from "./entityMetadataIndex";
import {
  appendToCompactCache,
  applyDependencyIndexBatch,
  bumpMetadataIndexVersion,
  entityMetadataIndex,
  getEntityFromCompactCache,
  getMetadataIndexVersion,
  purgeSpaceFromCaches,
  recordEntityProvenance,
  removeFromCompactCache,
  setInCompactCache,
  unmarkSpaceStreamed,
  type EntityProvenanceCommitContext,
} from "./mutationStore";
import { NO_MUTATIONS, type EntityMutation } from "./types";

/**
 * Track 1 / Phase 1 — transactional commit of a reducer's declared
 * mutations into the non-reactive cache + metadata index.
 *
 * Inputs:
 *   - prevDoc / nextDoc — full pre/post-dispatch documents (only the
 *     spaces affected by `mutations` are inspected; full doc is taken
 *     for parity with future reducer chains that may need it).
 *   - mutations — explicit `{ added, removed, updated, styleTablesTouched }`
 *     declared by the reducer (Pattern #3 from Track-1 web research).
 *
 * Side effects:
 *   - `compact cache` (post-V9 Section 0 Iter 6.D) — per-entity diffs
 *     are routed through `appendToCompactCache` /
 *     `removeFromCompactCache` / `setInCompactCache`. The legacy
 *     `entitiesCache: Map<spaceId, DrawingEntity[]>` write block was
 *     removed in this iteration; the reactive
 *     `geometryDocument.spaces[].entities` (slim-state-as-pointer,
 *     Phase 3b PR #6) is the only per-space entity owner now.
 *   - `entityMetadataIndex` — single `applyBatch` for all entity diffs.
 *     A block-table change (`prevDoc.blocks !== nextDoc.blocks`) additionally
 *     refreshes the derived bounds of every INSERT record whose definition (or
 *     a definition nested in it) changed, and a DIMSTYLE touch
 *     (`styleTablesTouched.has("dimensionStyles")`) those whose definition
 *     contains a `Dimension` — `blockRedefinitionMetadataRefresh.ts`.
 *   - `_version` — bumped exactly once for any non-no-op mutation.
 *
 * Returns `{ indexVersion, affectedSpaces, purgedEntityIds }`. Caller
 * forwards `indexVersion` into Zustand state to refresh React
 * subscribers and filters dead `purgedEntityIds` out of the selection.
 *
 * Phase 3b PR #6 consumes this from `editorStore.dispatch` — the
 * legacy resync allowlist has been retired.
 *
 * Track-1 plan: docs/plans/2026-05-06_state_layer_unification.md (v4),
 * Phase 1 §"API / типы" + GATE 3.
 */

export interface CommitOutput {
  indexVersion: number;
  affectedSpaces: ReadonlySet<string>;
  /**
   * Codex P2-3 (r5, PR #311) — entity ids purged through the
   * space-disappeared seam (`purgeSpaceFromCaches`). For a
   * streamed/cache-backed document the deleted space's entities exist
   * ONLY in the compact cache / metadata index (no shell previews), so
   * the shell reducer's preview-based `removedEntityIds` filter misses
   * exactly these ids — the dispatch pipeline drops them from
   * `selection.entityIds` / `previousPickset` after the resync. Empty
   * on every dispatch that does not remove a space.
   */
  purgedEntityIds: ReadonlySet<string>;
}

/**
 * Per-dispatch context that the cache/index commit itself does not need
 * but the DERIVED audit records it feeds do.
 *
 * Currently one field, forwarded verbatim into the entity-provenance
 * ledger — see {@link EntityProvenanceCommitContext}. Kept as an options
 * OBJECT (not a bare boolean parameter) so the next derived consumer that
 * needs dispatch context extends it instead of growing a positional
 * boolean chain.
 */
export type CommitMutationContext = EntityProvenanceCommitContext;

/**
 * Build the index-side `EntityMetadata` for a `DrawingEntity`. The
 * `spaceId` parameter takes precedence over `entity.spaceId` so that
 * mutations declaring a different containing space (e.g. a future
 * cross-space move) round-trip correctly.
 *
 * `blocks` — the committed document's block table (`nextDoc.blocks`), so a
 * `Block` (INSERT) entity's index bounds are its real transformed
 * definition extents (`blockExtentsCache.ts`), not its insertion point.
 * `dimensionStyles` — the committed document's name-keyed DIMSTYLE table
 * (`dimensionStyleTableFor(nextDoc.styleTables?.dimensionStyles)`), so a
 * `Dimension` child inside that definition contributes its NAMED-style
 * extents rather than AutoCAD's hardcoded DIMVAR defaults (Codex PR #784
 * round 3 P1). Both optional only for legacy test callers; production passes
 * them.
 */
export function toMetadata(
  entity: DrawingEntity,
  spaceId: string,
  blocks?: Record<string, BlockDefinition>,
  dimensionStyles?: DimensionStyleTable,
): EntityMetadata {
  const meta = entityMetadataFromDrawingEntity(entity, blocks, dimensionStyles);
  if (meta.spaceId === spaceId) return meta;
  return { ...meta, spaceId };
}

/**
 * The committed document's name-keyed DIMSTYLE table for {@link toMetadata}.
 * `dimensionStyleTableFor` memoizes per `styleTables.dimensionStyles` ARRAY
 * identity, so calling this once per metadata build costs one `WeakMap` read
 * — no per-entity re-keying (Codex PR #784 round 3 P1).
 */
function nextDimStyles(nextDoc: DrawingDocument): DimensionStyleTable | undefined {
  return dimensionStyleTableFor(nextDoc.styleTables?.dimensionStyles);
}

function isNoOp(mutations: EntityMutation): boolean {
  return (
    mutations.added.length === 0 &&
    mutations.removed.length === 0 &&
    mutations.updated.length === 0 &&
    mutations.styleTablesTouched.size === 0
  );
}

/**
 * Derived-bounds refresh of INSERT metadata records
 * (`blockRedefinitionMetadataRefresh.ts`), rebuilt against the COMMITTED
 * tables. TWO independent reasons an INSERT's stored box can go stale without
 * the INSERT entity itself being mutated — both resolved here, into ONE
 * refresh set:
 *
 *   1. BLOCK REDEFINITION — the referenced definition (or a definition nested
 *      in it) changed between `prevDoc.blocks` and `nextDoc.blocks`. Empty
 *      when the block table object is unchanged, so no index scan is paid on
 *      the common path.
 *   2. DIMSTYLE EDIT (Codex PR #784 round 4 P1) — a block-child `Dimension`'s
 *      extents come from its NAMED style's DIMVARs, and editing a DIMSTYLE
 *      replaces `styleTables.dimensionStyles` while leaving `blocks`
 *      IDENTICAL: reason 1 sees nothing, and a style-only mutation has no
 *      entity upserts either, so without this arm the stale RBush box survives
 *      the edit AND its undo/redo. TRIGGER — the DECLARED
 *      `styleTablesTouched.has("dimensionStyles")` signal, not a second
 *      identity compare: every writer of that table on the dispatch contour
 *      declares it (`styleTablesReducer.ts` — `styleTableTouchMutation` for
 *      add/update, `buildRemapMutation` for delete/rename, and the
 *      `header/restore-style-tables-snapshot` inverse recomputes the touch set
 *      by VALUE, so undo and redo take the same path), and unlike an identity
 *      compare it stays precise in dev/test, where `assembleDrawingDocument`'s
 *      Zod deep-clone gives every dispatch a new `dimensionStyles` array (the
 *      documented over-fire of the `blocks` compare in
 *      `blockRedefinitionMetadataRefresh.ts`). NARROWING — only definitions
 *      that actually contain a `Dimension`, directly or through a nested
 *      INSERT / MLEADER block content (`dimensionBearingBlockClosure`); a
 *      drawing with no dimensioned block pays one walk of the block table and
 *      no index scan at all.
 *
 * Entity resolution is the store's own read path, in the same order
 * `editorStore.getEntityById` uses: the compact cache (kept in sync by this
 * very function for every post-bootstrap mutation; `Block` is a compact
 * kind) first, then — only on a miss — the committed `nextDoc.spaces`
 * (`getEntityById` walks the store's document, which at this point is still
 * `prevDoc`). The walk is materialised as an id map at most once per commit
 * and only if a miss occurs; on the streamed path `spaces[].entities` are
 * empty, so it costs nothing there.
 *
 * Ids this commit already upserts (`added` / `updated` — built against
 * `nextDoc.blocks` in the caller) or deletes (`removed`) are excluded. For
 * an `updated` entry BOTH `prev.id` and `next.id` are excluded: a
 * type-conversion mutation may rename the id, and the stale `prev.id` row
 * must neither be resurrected nor rebuilt. ORDER MATTERS: this function runs
 * BEFORE the commit's own compact-cache writes, so the resolver's compact
 * read still sees the PRE-commit rows — safe only because every id this
 * commit declares is excluded above and therefore never resolved here.
 */
function blockBoundsRefreshUpserts(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
  mutations: EntityMutation,
): EntityMetadata[] {
  const changedBlocks = changedBlockDefinitionClosure(
    prevDoc.blocks,
    nextDoc.blocks,
  );
  if (mutations.styleTablesTouched.has("dimensionStyles")) {
    for (const name of dimensionBearingBlockClosure(nextDoc.blocks)) {
      changedBlocks.add(name);
    }
  }
  if (changedBlocks.size === 0) return [];
  const excludeIds = new Set<string>();
  for (const m of mutations.added) excludeIds.add(m.entity.id);
  for (const m of mutations.removed) excludeIds.add(m.entityId);
  for (const m of mutations.updated) {
    excludeIds.add(m.prev.id);
    excludeIds.add(m.next.id);
  }
  let committedById: Map<string, DrawingEntity> | null = null;
  const resolveEntity = (id: string): DrawingEntity | undefined => {
    const fromCompact = getEntityFromCompactCache(id);
    if (fromCompact && fromCompact.type !== "Attdef") return fromCompact;
    if (committedById === null) {
      committedById = new Map();
      for (const space of nextDoc.spaces) {
        for (const entity of space.entities) committedById.set(entity.id, entity);
      }
    }
    return committedById.get(id);
  };
  return blockInsertMetadataRefreshUpserts({
    index: entityMetadataIndex,
    changedBlocks,
    excludeIds,
    resolveEntity,
    buildMetadata: (entity, spaceId) =>
      toMetadata(entity, spaceId, nextDoc.blocks, nextDimStyles(nextDoc)),
  });
}

export function commitMutation(
  prevDoc: DrawingDocument,
  nextDoc: DrawingDocument,
  mutations: EntityMutation,
  context?: CommitMutationContext,
): CommitOutput {
  // post-V9 Section 0 Iter 6.D — the legacy `entitiesCache: Map` write
  // block (which walked `nextDoc.spaces` per affected space) was removed;
  // the compact cache + metadata index updates are driven entirely by the
  // per-entity diffs in `mutations`.
  //
  // LAYOUT-COMMAND-HANDLERS — the doc params ARE read for one purpose:
  // when a SPACE disappears between the documents (LAYOUT DELETE), its
  // streaming-discarded marker must be dropped so a future LAYOUT NEW
  // re-using the same minted id is not mis-flagged cache-authoritative,
  // AND (Codex P2-5) every cached row of the removed space must be
  // purged from the compact store + metadata index + dependency index.
  // For a streamed import the deleted space's inline `space.entities`
  // is EMPTY — its entities live ONLY in the compact store — so the
  // reducer's `mutations.removed` diff sees nothing; without the purge
  // the dead rows leak and resurface via every compact reader.
  // Checked BEFORE the no-op early-out (an EMPTY deleted layout yields
  // zero entity mutations). Length compare first — O(1) on the common
  // path; the id-set walk runs only when the space set actually shrank.
  // Defect fix (command-created-line-provenance) — record real entity
  // provenance from THIS commit's declared mutations before anything
  // else runs. `commitMutation` is the single choke point for every
  // post-bootstrap entity add/remove/update (ПРИОРИТЕТ #2.1); the
  // import/bootstrap pipeline never calls it (see `recordEntityProvenance`'s
  // doc comment in `mutationStore.ts`), so this is the one place that can
  // tell a command/agent/collab-created entity apart from an imported one.
  // `context.historyReplay` (the dispatch's `__bypassHistory` flag) is the
  // restore-vs-fresh-authorship discriminator — reads NOTHING from either
  // document, so a removal never materialises a space (GitHub Codex review
  // PR #582 findings 1 + 3).
  recordEntityProvenance(mutations, context);

  const purgedSpaces = new Set<string>();
  const purgedEntityIds = new Set<string>();
  if (prevDoc.spaces.length > nextDoc.spaces.length) {
    const nextSpaceIds = new Set(nextDoc.spaces.map((space) => space.id));
    for (const space of prevDoc.spaces) {
      if (!nextSpaceIds.has(space.id)) {
        unmarkSpaceStreamed(space.id);
        const purgedIds = purgeSpaceFromCaches(space.id);
        if (purgedIds.size > 0) {
          purgedSpaces.add(space.id);
          for (const id of purgedIds) purgedEntityIds.add(id);
        }
      }
    }
  }

  // Derived INSERT-bounds refresh — computed BEFORE the no-op early-out: a
  // BSAVE / BCLOSE-save / redefine / attribute sync replaces `blocks`
  // without any entity-level mutation (the `space/*` diff excludes the
  // synthetic edit space), and a DIMSTYLE edit replaces only the style table,
  // yet every affected INSERT has stale derived bounds in the index. See
  // `blockBoundsRefreshUpserts`.
  const blockRefreshUpserts = blockBoundsRefreshUpserts(
    prevDoc,
    nextDoc,
    mutations,
  );

  if (isNoOp(mutations)) {
    if (blockRefreshUpserts.length > 0) {
      entityMetadataIndex.applyBatch({
        upserts: blockRefreshUpserts,
        deletes: [],
      });
    }
    const affectedSpaces = new Set<string>(purgedSpaces);
    for (const u of blockRefreshUpserts) affectedSpaces.add(u.spaceId);
    return {
      // A streamed-space purge (or a derived INSERT-bounds refresh) mutates
      // the metadata index even though the reducer declared no entity
      // mutations — bump the version so React subscribers latched onto
      // the index re-evaluate.
      indexVersion:
        purgedSpaces.size > 0 || blockRefreshUpserts.length > 0
          ? bumpMetadataIndexVersion()
          : getMetadataIndexVersion(),
      affectedSpaces,
      purgedEntityIds,
    };
  }

  const affectedSpaces = new Set<string>(purgedSpaces);
  for (const u of blockRefreshUpserts) affectedSpaces.add(u.spaceId);
  for (const m of mutations.added) affectedSpaces.add(m.spaceId);
  for (const m of mutations.removed) affectedSpaces.add(m.spaceId);
  for (const m of mutations.updated) affectedSpaces.add(m.spaceId);

  // post-V9 Section 0 Iter 6.D — sync mutations into the compact cache.
  // Iterates the per-entity diffs declared by the reducer (compact
  // cache is per-entity, not per-space). The legacy
  // `entitiesCache: Map<spaceId, DrawingEntity[]>` write block was
  // removed in this iteration; reactive `space.entities` (Phase 3b PR
  // #6 slim-state-as-pointer) is the only per-space owner now.
  //
  // Each mutation kind maps to one helper:
  //   * added   → appendToCompactCache  (returns -1 for unsupported types,
  //                                       which is fine — those entities
  //                                       were already absent from the
  //                                       compact cache for the same
  //                                       reason, e.g. "Other"/Block-def-
  //                                       only entries)
  //   * removed → removeFromCompactCache (no-op if id not in compact —
  //                                       e.g. removed entity was an
  //                                       unsupported type)
  //   * updated → setInCompactCache     (-1 if id not in compact; we then
  //                                       fall through to appendToCompactCache
  //                                       so a previously-skipped entity
  //                                       newly typed-as-supported still
  //                                       lands in the store)
  //
  // Tombstones accumulated by remove/setInCompactCache trigger
  // `maybeCompactCompactCache()` inside the helpers themselves.
  for (const m of mutations.added) {
    appendToCompactCache(m.entity);
  }
  for (const m of mutations.removed) {
    removeFromCompactCache(m.entityId);
  }
  for (const m of mutations.updated) {
    // Look up by the PREVIOUS id so id-renames (legal in type-conversion
    // mutations) clean the stale entry; setInCompactCache itself handles
    // the `entityId !== nextEntity.id` case by deleting the old idIndex
    // mapping. If the prev id wasn't indexed (small-drawing path that
    // didn't populate compact at import time, or unsupported→supported
    // type change), fall through to `appendToCompactCache` so the new
    // entity still lands in the store.
    const idx = setInCompactCache(m.prev.id, m.next);
    if (idx < 0) {
      appendToCompactCache(m.next);
    }
  }

  const upserts: EntityMetadata[] = [];
  const deletes: string[] = [];
  for (const m of mutations.removed) deletes.push(m.entityId);
  for (const m of mutations.added) {
    // Phase 2 Wave 2 Task 3 — fresh inserts start at geomRev=0. The
    // tessellation cache will compute + store the buffer on first
    // request; subsequent geometry-only updates bump this counter.
    upserts.push({
      ...toMetadata(m.entity, m.spaceId, nextDoc.blocks, nextDimStyles(nextDoc)),
      geomRev: 0,
    });
  }
  for (const m of mutations.updated) {
    // Phase 2 Wave 2 Task 3 — only bump geomRev when the diff against
    // the previous entity classifies as "geometry" or "both". Style /
    // other changes preserve the existing rev so the tessellation
    // cache stays valid.
    const prevMeta = entityMetadataIndex.get(m.prev.id);
    const prevRev = prevMeta?.geomRev ?? 0;
    const klass = classifyEntityChange(m.prev, m.next);
    const nextRev =
      klass === "geometry" || klass === "both" ? prevRev + 1 : prevRev;
    upserts.push({
      ...toMetadata(m.next, m.spaceId, nextDoc.blocks, nextDimStyles(nextDoc)),
      geomRev: nextRev,
    });
  }

  // The derived INSERT-bounds refresh rides the SAME applyBatch (one index
  // write per commit). Its ids are disjoint from the declared mutations' ids
  // (excluded in `blockBoundsRefreshUpserts`), so no record is upserted
  // twice or resurrected after a delete.
  for (const u of blockRefreshUpserts) upserts.push(u);

  if (upserts.length > 0 || deletes.length > 0) {
    entityMetadataIndex.applyBatch({ upserts, deletes });
  }

  // PR2 (plan §13) — incremental dependency-index update from the SAME
  // per-entity diffs. added → insert refs (full entity available); removed →
  // delete by (container=spaceId, id) (id-only); updated → diff prev→next. The
  // index extracts the entity's layer/style/block/linetype refs at mutation
  // time, so a layer-move (X→Y) drops the entity from X's dependents and adds
  // it to Y's without a full rebuild. Hooked AFTER the metadata applyBatch so
  // both indexes update in lockstep on the single commit choke point.
  applyDependencyIndexBatch(mutations);

  return {
    indexVersion: bumpMetadataIndexVersion(),
    affectedSpaces,
    purgedEntityIds,
  };
}

/**
 * Phase 3b PR #6 — concatenate two `EntityMutation` records.
 *
 * Style-table touch sets are unioned. Entity-level arrays are
 * preserved in `(a, then b)` order. `NO_MUTATIONS` short-circuits to
 * the other operand without copying. Used by `editorStore.dispatch`
 * to merge mutations from multiple reducer passes (e.g. annotative
 * shell sub-reducer + main geometry reducer + style CRUD path) into
 * a single payload before calling `commitMutation`.
 *
 * Mirrors the local `mergeMutations` helper inside `geometryReducer.ts`
 * (which folds inner + mirror-diff). Hoisted here so cross-module
 * callers don't reach into the reducer's private namespace.
 */
export function mergeMutations(
  a: EntityMutation,
  b: EntityMutation,
): EntityMutation {
  if (a === NO_MUTATIONS) return b;
  if (b === NO_MUTATIONS) return a;
  const styleTables = new Set(a.styleTablesTouched);
  for (const k of b.styleTablesTouched) styleTables.add(k);
  return {
    added: [...a.added, ...b.added],
    removed: [...a.removed, ...b.removed],
    updated: [...a.updated, ...b.updated],
    styleTablesTouched: styleTables,
  };
}

/**
 * Phase 3b PR #6 — true if `mutations` describes no entity-level OR
 * style-table change. Used by `syncShellStateFromGeometryDocument` to
 * fast-skip the expensive shell-preview rebuild on dispatch passes
 * that the geometry reducer reports as no-ops (e.g. selection-only,
 * UI-only, or audit-only commands).
 */
export function isNoOpMutation(mutations: EntityMutation): boolean {
  return isNoOp(mutations);
}

/**
 * Lane J (D3-1) fast-path predicate — true when the mutation touched NO
 * entity (`added` / `removed` / `updated` all empty), REGARDLESS of
 * `styleTablesTouched`. A style/layer/linetype-table-only edit (layer
 * colour, DIMSTYLE / MLEADERSTYLE value, text-style, LTYPE) satisfies this:
 * the geometry document's ENTITIES are byte-for-byte unchanged, so the
 * O(all-entities) shell entity-preview rebuild in
 * `syncShellStateFromGeometryDocument` is pure waste — the previews still
 * mirror the document and the enrichment pipeline (which runs regardless of
 * the skip) re-projects the new DIMVAR / MLVAR values. This is the SINGLE
 * "were entities touched?" signal shared by the dispatch fast-paths that the
 * pre-Lane-J direct-set setters got for free via `skipEntityRebuild: true`
 * (preview reuse) and by never snapshotting/diffing spaces for a table-only
 * edit. `isNoOpMutation` (which ALSO requires `styleTablesTouched.size === 0`)
 * is the STRICTER "nothing changed at all" predicate used by
 * `commitMutation`'s own cache/index early-out — do not conflate the two.
 */
export function isEntityLevelNoOpMutation(mutations: EntityMutation): boolean {
  return (
    mutations.added.length === 0 &&
    mutations.removed.length === 0 &&
    mutations.updated.length === 0
  );
}
